import React, {useEffect, useMemo, useRef, useState} from 'react';
import {stripSignedUrlQuery} from '../../../../Utilities/utilFunctions';
import useRemintAsset from '../../../../../hooks/useRemintAsset';
import { isAssetValid, AUDIO_BUFFER_SEC } from '../../../../../utils/assetExpiry';

interface WebAudioPlayerProps {
  uri: string;
  stopAudioPlayer?: boolean;
  contentBaseUrl?: string;
  onEnded?: () => void;
}

const WebAudioPlayer: React.FC<WebAudioPlayerProps> = ({
  uri,
  stopAudioPlayer = false,
  contentBaseUrl = '',
  onEnded,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [activeUri, setActiveUri] = useState(uri);
  // Incremented when remint delivers a genuinely fresh token so the Audio
  // lifecycle effect reruns even though the content path (contentKey) is unchanged.
  const [tokenVersion, setTokenVersion] = useState(0);
  const { remint } = useRemintAsset();

  // On URI change: adopt immediately for content-key change detection, then
  // asynchronously remint if the token is expired/about to expire.  When remint
  // returns a different URL (fresh token), bump tokenVersion so the lifecycle
  // effect recreates the Audio element with the working URL.
  useEffect(() => {
    if (!uri) { setActiveUri(uri); return; }
    setActiveUri(uri);
    if (!isAssetValid(uri, AUDIO_BUFFER_SEC)) {
      remint([uri]).then(([fresh]) => {
        if (fresh && fresh !== uri) {
          setActiveUri(fresh);
          setTokenVersion(v => v + 1);
        }
      });
    }
  }, [uri]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolvedUrl = useMemo(() => {
    let audioUrl = activeUri || '';
    if (activeUri?.includes('/api/getAudio/output') && contentBaseUrl) {
      audioUrl = `${contentBaseUrl}${activeUri}`;
    }
    return audioUrl;
  }, [activeUri, contentBaseUrl]);

  // Holds the latest resolvedUrl so the lifecycle effect always plays the freshest
  // URL — including the one just returned by remint — without that full URL being
  // a direct dep (which would cause needless recreation on every token refresh).
  const resolvedUrlRef = useRef(resolvedUrl);
  resolvedUrlRef.current = resolvedUrl;

  // Content key: derived from the *prop* URI so content changes are detected
  // immediately, independent of when remint resolves.
  const contentKey = uri
    ? stripSignedUrlQuery(
        uri.includes('/api/getAudio/output') && contentBaseUrl
          ? `${contentBaseUrl}${uri}`
          : uri,
      )
    : '';

  // Manage Audio element lifecycle whenever the underlying content changes OR
  // when remint delivers a fresh token for a previously-expired URL (tokenVersion).
  useEffect(() => {
    const audioUrl = resolvedUrlRef.current;
    if (!audioUrl) {
      return;
    }

    const audio = new Audio(audioUrl);
    audioRef.current = audio;
    if (onEnded) {
      audio.addEventListener('ended', onEnded);
    }
    if (!stopAudioPlayer) {
      audio.play().catch(() => undefined);
    }

    return () => {
      audio.pause();
      if (onEnded) audio.removeEventListener('ended', onEnded);
      audioRef.current = null;
    };
  }, [contentKey, tokenVersion, stopAudioPlayer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (stopAudioPlayer && audioRef.current) {
      audioRef.current.pause();
    }
  }, [stopAudioPlayer]);

  return null;
};

export default WebAudioPlayer;
